import { type User } from '@privy-io/react-auth'

interface AuthMethod {
  type: string
  value: string | null
}

interface SocialAccount {
  id?: string
  username?: string
  email?: string
  subject?: string
  address?: string
  type?: string
}

export const getAuthMethod = (
  user: User,
  authMethod: string | null
): AuthMethod => {
  if (!user || !authMethod) {
    return { type: 'none', value: null }
  }

  // Handle OAuth methods
  if (authMethod.includes('_oauth')) {
    const oauthType = authMethod.replace('_oauth', '')
    const account = user[oauthType] as SocialAccount | undefined
    if (account) {
      if (['google', 'apple'].includes(oauthType)) {
        return { type: oauthType, value: account.email || null }
      }
      if (['twitter', 'discord'].includes(oauthType)) {
        return { type: oauthType, value: account.username || null }
      }
      return { type: oauthType, value: account.subject || account.id || null }
    }
  }

  // Handle direct methods
  switch (authMethod) {
    case 'email':
      return { type: 'email', value: user.email?.address || null }
    case 'google':
    case 'apple':
      return {
        type: authMethod,
        value: (user[authMethod] as SocialAccount)?.email || null,
      }
    case 'twitter':
    case 'discord':
      return {
        type: authMethod,
        value: (user[authMethod] as SocialAccount)?.username || null,
      }
    case 'github':
    case 'linkedin':
    case 'spotify':
    case 'instagram':
    case 'telegram':
    case 'tiktok':
    case 'farcaster':
      const account = user[authMethod] as SocialAccount
      return {
        type: authMethod,
        value: account?.id || account?.username || null,
      }
    case 'wallet':
    case 'smart_wallet':
      return { type: authMethod, value: user[authMethod]?.address || null }
    default:
      // Check linkedAccounts for other methods
      if (user.linkedAccounts && Array.isArray(user.linkedAccounts)) {
        const linkedAccount = user.linkedAccounts.find(
          acc => acc.type === authMethod
        ) as SocialAccount | undefined
        if (linkedAccount) {
          return {
            type: authMethod,
            value:
              linkedAccount.address ||
              linkedAccount.email ||
              linkedAccount.subject ||
              linkedAccount.id ||
              null,
          }
        }
      }
      return { type: 'none', value: null }
  }
}
